// src/components/modals/DeleteUnitModal.jsx
import { useState } from 'react'
import { Button } from '../ui/Button'
import { X, AlertTriangle, Loader2 } from 'lucide-react'
import UnitsService from '../../services/unitsService'

const DeleteUnitModal = ({ isOpen, onClose, onUnitDeleted, unit }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Handle delete confirmation
  const handleDelete = async () => {
    if (!unit?._id) {
      console.error('❌ No unit ID provided for deletion')
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      // Enhanced debug logging
      console.log('🗑️ Deleting unit:', {
        unitId: unit._id,
        unitName: unit.name,
        token: localStorage.getItem('tezlaa-access-token') ? 'Present' : 'Missing'
      })
      
      const response = await UnitsService.deleteUnit(unit._id)
      
      if (response.success) {
        console.log('✅ Unit deleted successfully:', response)
        
        // Notify parent component
        onUnitDeleted(unit._id)
        
        // Close modal
        onClose()
      } else {
        console.log('⚠️ Delete failed with response:', response)
        setError(response.message || 'Failed to delete unit')
      }
    } catch (error) {
      console.error('❌ Error deleting unit:', error)
      
      // Enhanced error handling
      let errorMessage = 'Failed to delete unit. Please try again.'
      
      if (error.message.includes('Not found')) {
        errorMessage = 'Unit not found. It may have already been deleted.'
      } else if (error.message.includes('Unauthorized')) {
        errorMessage = 'You are not authorized to delete this unit. Please login again.'
      } else if (error.message.includes('Network')) {
        errorMessage = 'Network error. Please check your connection and try again.'
      } else if (error.message.includes('conflict') || error.message.includes('in use')) {
        errorMessage = 'Cannot delete unit. It may be in use by products.'
      } else if (error.message.includes('products exist')) {
        errorMessage = 'Cannot delete unit. Please update products using this unit first.'
      }
      
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  // Handle modal close
  const handleClose = () => {
    if (!loading) {
      setError(null)
      onClose()
    }
  }

  // Don't render if not open
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Confirmation</h3>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="mb-6">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete this? This action cannot be undone.
            </p>
            
            {/* Unit Info */}
            {unit && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <span className="text-lg">{UnitsService.getTypeIcon(unit.type)}</span>
                  <div>
                    <p className="font-medium text-gray-900">{unit.name}</p>
                    <p className="text-sm text-gray-600">
                      {unit.abbreviation} • {unit.type}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Warning */}
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium">Warning:</p>
                  <p>Deleting this unit may affect products that use it. Make sure to update those products first.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-red-400 mr-2 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={loading}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DeleteUnitModal